import {useQuery, useQueries, useQueryClient} from "@tanstack/react-query"
import supabase from "@/supabaseConfig"

interface UserInfo {
  id: string
  name: string
  email?: string
}

interface UseUserInfoReturn {
  userInfo: UserInfo | null
  loading: boolean
  error: Error | null
}

interface UseUsersInfoReturn {
  users: UserInfo[]
  loading: boolean
  errors: Error[]
}

interface UsePrefetchUserReturn {
  prefetchUser: (userId: string) => void
}

/**
 * Fetches user info by auth_user_id
 */
const fetchUserById = async (userId: string): Promise<UserInfo> => {
  try {
    // Try to get user info from the user dashboard
    const {data, error} = await supabase
      .from("user_dashboard")
      .select("full_name, email")
      .eq("auth_user_id", userId)
      .single()

    if (data && !error) {
      return {
        id: userId,
        name: data.full_name || data.email || `User ${userId.slice(0, 8)}`,
        email: data.email || undefined,
      }
    }
  } catch (err) {
    console.warn("Could not fetch user name:", err)
  }

  // Fallback: just show the first 8 characters of the user_id
  return {
    id: userId,
    name: `User ${userId.slice(0, 8)}`,
  }
}

/**
 * Hook to get user information for a specific user ID
 *
 * Caching is done to avoid repeated requests to the database, especially since one user
 * will often perform multiple actions on alerts reviews. Uses TanStack Query for
 * automatic caching, deduplication, and background updates.
 */
export function useUserInfo(userId: string | null): UseUserInfoReturn {
  const query = useQuery({
    queryKey: ["user", userId],
    queryFn: () => fetchUserById(userId!),
    enabled: !!userId, // Only run if userId exists
    staleTime: 5 * 60 * 1000, // Data stays fresh for 5 minutes
    gcTime: 10 * 60 * 1000, // Keep in cache for 10 minutes after last use
    retry: 1, // Only retry once on failure to avoid repeated failed requests
  })

  return {
    userInfo: query.data || null,
    loading: query.isLoading,
    error: query.error,
  }
}

/**
 * Hook to get multiple users at once - useful for batch loading
 * Makes parallel requests but caches each user individually.
 */
export function useUsersInfo(userIds: (string | null)[]): UseUsersInfoReturn {
  const validUserIds = userIds.filter((id): id is string => !!id)

  const queries = useQueries({
    queries: validUserIds.map((userId) => ({
      queryKey: ["user", userId],
      queryFn: () => fetchUserById(userId),
      staleTime: 5 * 60 * 1000,
      gcTime: 10 * 60 * 1000,
      retry: 1,
    })),
  })

  return {
    users: queries.map((q) => q.data).filter(Boolean) as UserInfo[],
    loading: queries.some((q) => q.isLoading),
    errors: queries.map((q) => q.error).filter(Boolean),
  }
}

/**
 * Helper hook to prefetch user data - useful when you know you'll need it soon
 *
 * Good for improving UX in scenarios like:
 * - Hovering over user mentions/avatars
 * - Navigating to pages that will show user info
 * - Preloading data for likely user interactions
 */
export function usePrefetchUser(): UsePrefetchUserReturn {
  const queryClient = useQueryClient()

  const prefetchUser = (userId: string) => {
    queryClient.prefetchQuery({
      queryKey: ["user", userId],
      queryFn: () => fetchUserById(userId),
      staleTime: 5 * 60 * 1000,
      gcTime: 10 * 60 * 1000,
      retry: 1,
    })
  }

  return {prefetchUser}
}
